import React, { useEffect, useRef, useState } from "react"
import { ThemeConsumer } from "@rneui/themed"
import { FlatList, StyleSheet, TextInput, TouchableOpacity, useWindowDimensions, View } from "react-native"
import { ZenIcon, ZenText } from "zen-core-elements";
import { Theme } from "@rneui/themed";
import SellscreenModal from "../../sellscreenModal";
import { getPaymentMode, isCustomerOnAccountEligible, PAYMENT_METHODS } from "../../../../redux/utils/discountUtils";
import CashPayment from "./cashPayment";
import PaymentNotes from "./paymentNotes";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../redux/hooks";
import { config } from "../../../../shared/environment";
import { setTransactionData } from "../../../../redux/slices/newSellscreenPaymentSlice";
import CommonService from "../../../../shared/services/commonService";
import { paymentSaveType, sellScreenPaymentInitialStateType } from "../../../../redux/types/sellScreenTypes";
import GiftCardPayment from "./giftCartPayment";
import StoreCreditPayment from "./storeCreditPayment";
import OnAccountPayment from "./onAccountPayment";
import { webDialogWidth } from '../../../../platform/design/dialogWeb';

const PaymentMethods = ({ modalOpen, onClose, editPayment }: { modalOpen: boolean, onClose: () => void, editPayment?: sellScreenPaymentInitialStateType["transactionData"][0] }) => {
	const inputRef = useRef<TextInput>(null);
	const { width, height } = useWindowDimensions()
	const [paymentMethod, setPaymentMethod] = useState<typeof PAYMENT_METHODS[0]["id"]>("")

	const dispatch = useDispatch()
	const { totalAmount, transactionData, remainingAmount } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const { customerDetails } = useAppSelector((state) => state?.NewSellscreenSlice)
	const checkoutGiftCart = useAppSelector((state) => state?.sellScreen?.checkoutGiftCart)
	const isBuyingGiftCard = Boolean(checkoutGiftCart?.giftCardValue?.value || checkoutGiftCart?.giftCardValue)

	useEffect(() => {
		if (editPayment) {
			setPaymentMethod(editPayment?._paymentMethod)
		}
	}, [editPayment])

	const handleSelectMethod = (id: typeof PAYMENT_METHODS[0]["id"]) => {
		setTimeout(() => inputRef?.current?.focus(), 100)
		setPaymentMethod(id)
	}

	const handleSave = ({ paidAmount, reason = "", amount, giftCardPayment = null, giftCardId = null, storeCreditId = null }: paymentSaveType) => {
		dispatch(setTransactionData({
			data: {
				storeId: config?.storeId,
				paymentMode: getPaymentMode(paymentMethod),
				paymentStatus: storeCreditId ? "PENDING" : "SUCCESS",
				amount,
				reason,
				giftCardId,
				storeCreditId,
				_completed: true,
				_id: new Date().getTime().toString(),
				_paymentMethod: paymentMethod,
				_paidAmount: paidAmount,
				_giftCardPayment: giftCardPayment
			},
			id: editPayment?._id
		}))
		setPaymentMethod('')
		onClose()
	}

	const onPaymentClose = () => {
		setPaymentMethod("")
		onClose()
		inputRef?.current?.blur()
	}

	const amountToCheck = editPayment ? (remainingAmount + editPayment._paidAmount) : remainingAmount;
	const availablePaymentMethods = PAYMENT_METHODS.filter((item) => {
		if (isBuyingGiftCard && (item.id === "Gift Card" || item.id === "Store Credit" || item.id === "On Account")) {
			return false;
		}
		if (item.id === "On Account") {
			return isCustomerOnAccountEligible(customerDetails, amountToCheck);
		}
		return true;
	});

	const renderItem = ({ item }: { item: typeof PAYMENT_METHODS[0] }) => (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				if (item?.id == "") {
					return <View style={{ flexGrow: 1, margin: 8, padding: 20 }}></View>
				}
				console.log(item);

				const isDisabled = (item?.id == "Online Payment" && transactionData?.some((item) => item?._paymentMethod == "Online Payment") && editPayment?._paymentMethod !== "Online Payment") ||
					(item?.id == "Cash" && transactionData?.some((item) => item?._paymentMethod == "Cash") && editPayment?._paymentMethod !== "Cash")
					|| (item?.id == "Store Credit" && transactionData?.some((item) => item?._paymentMethod == "Store Credit") && editPayment?._paymentMethod !== "Store Credit") || (item?.id == "On Account" && transactionData?.some((item) => item?._paymentMethod == "On Account") && editPayment?._paymentMethod !== "On Account")

				return (
					<TouchableOpacity
						disabled={isDisabled}
						style={[styles.gridItem, isDisabled && { opacity: 0.3 }]}
						onPress={() => handleSelectMethod(item?.id as typeof PAYMENT_METHODS[0]["id"])}
					>
						<View style={[styles.gridIcon, { backgroundColor: item.color + '10' }]}>
							<ZenIcon name={item.icon} type="material-community" size={32} color={item.color} />
						</View>
						<ZenText content={item.id} style={styles.gridText} />
					</TouchableOpacity>
				)
			}}
		</ThemeConsumer>
	)

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<SellscreenModal onShow={() => inputRef?.current?.focus()} isModalOpen={modalOpen} onCloseRequest={onPaymentClose}>
						<View style={[{ minWidth: width > 900 ? width * 0.5 : width > 600 ? width * 0.60 : width * 0.80, maxHeight: width > 900 ? height * 0.8 : height * 0.60, }, webDialogWidth('lg', width)]}>
							<View style={styles.header}>
								<View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
									{paymentMethod ? <ZenIcon name={"arrow-left"} type={"material-community"} size={width > 600 ? 28 : 22} color={theme?.dark} onPress={() => setPaymentMethod("")} /> : null}
									<ZenText content={(paymentMethod || "Select") + " Payment"} style={styles.headerTitle} />
								</View>
								<TouchableOpacity onPress={onPaymentClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
									<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
								</TouchableOpacity>
							</View>
							{paymentMethod == "" ? <FlatList
								data={[...availablePaymentMethods, { id: "", icon: "", color: "" }]}
								numColumns={3}
								keyExtractor={(_, index) => index.toString()}
								renderItem={renderItem}
								contentContainerStyle={{ paddingTop: 20 }}
							/> :
								paymentMethod == "Cash" ?
									<CashPayment
										inputRef={inputRef}
										handleSave={handleSave}
										onClose={onPaymentClose}
										initialValue={editPayment?._paidAmount}
									/> :
									// paymentMethod == "Gift Card" ?
									// 	<GiftCardPayment
									// 		inputRef={inputRef}
									// 		handleSave={handleSave}
									// 		onClose={onPaymentClose}
									// 		initialValue={editPayment?._paidAmount}
									// 		giftCardPayment={editPayment?._giftCardPayment}
									// 	/>
									// 	:
									// 	paymentMethod == "Store Credit" ? <StoreCreditPayment
									// 		inputRef={inputRef}
									// 		handleSave={handleSave}
									// 		onClose={onPaymentClose}
									// 		initialValue={editPayment?._paidAmount}
									// 		notes={editPayment?.reason}
									// 	/>
									// 		:
									// 		paymentMethod == "On Account" ? <OnAccountPayment
									// 			inputRef={inputRef}
									// 			handleSave={handleSave}
									// 			onClose={onPaymentClose}
									// 			initialValue={editPayment?._paidAmount}
									// 			notes={editPayment?.reason}
									// 		/>
									// 			:
									<PaymentNotes
										inputRef={inputRef}
										handleSave={handleSave}
										onClose={onPaymentClose}
										paymentMethod={paymentMethod}
										initialValue={editPayment?._paidAmount}
										notes={editPayment?.reason}
									/>

							}
						</View>
					</SellscreenModal>
				)
			}}
		</ThemeConsumer>
	)
}

const style = (theme: Theme) => StyleSheet.create({
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		// paddingHorizontal: 24,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: theme?.softSlate,
	},
	headerTitle: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},
	gridItem: { flex: 1, margin: 8, padding: 20, backgroundColor: theme?.iceWhite, borderRadius: 24, alignItems: 'center', borderWidth: 1, borderColor: theme?.softSlate },
	gridIcon: { width: 60, height: 60, borderRadius: 200, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
	gridText: { fontSize: theme?.smallHeaderFontSize, fontFamily: theme?.medium, color: theme?.lightDark }
})

export default PaymentMethods